<template lang="pug">
.ip-domain-validation
    p.empty-text(v-if="!ipDomainList.length") {{ $t("ipDomainCerts.noIpDomain") }}
    template(v-else)
        .summary
            section
                Tag(color="success") {{ summaryText }}
                Checkbox(v-if="isValidated === false" v-model="showUnvalidatedOnly" style="margin-left: 12px")
                    | {{ $t("ipDomainCerts.showUnvalidatedOnly") }}
            Button(size="small" @click="updateValidityStatus") {{ $t("ipDomainCerts.updateValidityStatus") }}
        ul.ip-list
            li.ip-row(v-for="item in pagedList" :key="item.ip")
                span.ip-text {{ item.ip }}
                span.status.validated(v-if="item.validated")
                    | ✅ {{ $t("ipDomainCerts.validatedText") }}
                span.status.pending(v-else)
                    | — {{ $t("ipDomainCerts.pendingText") }}
        Page(
            v-if="filteredList.length > pageSize"
            :total="filteredList.length"
            :page-size="pageSize"
            :current="currentPage"
            size="small"
            @on-change="currentPage = $event"
        )
</template>

<script>
function ipToInt(ip) {
    const parts = ip.split(".");
    if (parts.length !== 4) return null;
    let result = 0;
    for (let i = 0; i < 4; i++) {
        const octet = Number(parts[i]);
        if (!Number.isInteger(octet) || octet < 0 || octet > 255) return null;
        result = result * 256 + octet;
    }
    return result >>> 0;
}

function intToIp(int) {
    return [(int >>> 24) & 255, (int >>> 16) & 255, (int >>> 8) & 255, int & 255].join(".");
}

// 解析單一 CIDR/IP，回傳 { base, count } 或 null
function parseCidr(entry) {
    const trimmed = String(entry).trim();
    if (!trimmed) return null;
    const [ipPart, prefixPart] = trimmed.split("/");
    const base = ipToInt(ipPart);
    if (base === null) return null;
    let prefix = prefixPart === undefined ? 32 : Number(prefixPart);
    if (!Number.isInteger(prefix) || prefix < 0 || prefix > 32) return null;
    const count = Math.pow(2, 32 - prefix);
    const mask = prefix === 0 ? 0 : (0xffffffff << (32 - prefix)) >>> 0;
    const network = (base & mask) >>> 0;
    return { base: network, count };
}
import { getEsData } from "@/api/esdata";

export default {
    name: "IpDomainValidationStatus",
    props: {
        formData: { type: Object, default: () => ({}) },
        formItem: { type: Object, default: () => ({}) },
        value: { type: [String, Array, Number], default: "" }
    },
    data() {
        return {
            showUnvalidatedOnly: false,
            currentPage: 1,
            pageSize: 50
        };
    },
    computed: {
        ipDomainList() {
            const list = this.formData.ipDomainList;
            return Array.isArray(list) ? list : [];
        },
        validatedSet() {
            const list = this.formData.validatedIp;
            return new Set(Array.isArray(list) ? list.map(ip => String(ip).trim()) : []);
        },
        ranges() {
            return this.ipDomainList.map(parseCidr).filter(r => r !== null);
        },
        totalCount() {
            return this.ranges.reduce((sum, r) => sum + r.count, 0);
        },
        validatedCount() {
            return this.validatedSet.size;
        },
        summaryText() {
            return this.$t("ipDomainCerts.validationSummary", {
                validated: this.validatedCount,
                total: this.totalCount
            });
        },
        isValidated() {
            return this.validatedCount === this.totalCount;
        },
        // 完整展開的 IP 清單（含驗證狀態）
        fullList() {
            const result = [];
            this.ranges.forEach(range => {
                for (let i = 0; i < range.count; i++) {
                    const ip = intToIp((range.base + i) >>> 0);
                    result.push({ ip, validated: this.validatedSet.has(ip) });
                }
            });
            return result;
        },
        filteredList() {
            if (this.showUnvalidatedOnly) {
                return this.fullList.filter(item => !item.validated);
            }
            return this.fullList;
        },
        pagedList() {
            const start = (this.currentPage - 1) * this.pageSize;
            return this.filteredList.slice(start, start + this.pageSize);
        }
    },
    watch: {
        showUnvalidatedOnly() {
            this.currentPage = 1;
        },
        "formData.ipDomainList"() {
            this.currentPage = 1;
        }
    },
    methods: {
        updateValidityStatus() {
            const searchOption = {
                isFuzzySearch: false,
                esIndex: "rcm-certpems",
                from: 0,
                size: 1,
                sort: [
                    {
                        modifiedDate: {
                            order: "desc",
                        },
                    },
                ],
                filters: [
                    {
                        key: "groupName",
                        type: "string",
                        value: this.formData.groupName,
                    },
                    {
                        key: "keypairname",
                        type: "string",
                        value: this.formData.keypairname,
                    },
                ],
                searchView: "IpDomainCertsApply",
            };
            getEsData(searchOption)
                .then((res) => {
                    const validatedIp = res.data.mapData.flatMap((item) => item.validatedIp || []);
                    this.$emit("updateFormData", { ...this.formData, validatedIp });
                })
                .catch((err) => console.log(err));
        },
    },
};
</script>

<style lang="less" scoped>
.ip-domain-validation {
    .summary {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 8px;
    }
    .limit-note {
        color: #ff9900;
        font-size: 12px;
        margin-bottom: 8px;
    }
    .ip-list {
        list-style: none;
        margin: 0;
        padding: 0;
        border: 1px solid #e8eaec;
        border-radius: 4px;
        max-height: 360px;
        overflow-y: auto;
    }
    .ip-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 6px 12px;
        border-bottom: 1px solid #f0f0f0;
        font-family: monospace;
    }
    .ip-row:last-child {
        border-bottom: none;
    }
    .status.validated {
        color: #19be6b;
    }
    .status.pending {
        color: #999;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/IpDomainValidationStatus.vue